/**
 * AnalyticsDashboard.jsx - Admin Analytics Dashboard
 * 
 * @description
 * Comprehensive analytics dashboard for admin panel showing both GA4 and custom metrics.
 * Includes charts, KPIs, and detailed user behavior analytics.
 * 
 * @features
 * - Real-time analytics data
 * - Interactive charts
 * - Key performance indicators
 * - User behavior insights
 * - Link performance metrics
 * - Conversion tracking
 */

import { useState, useEffect, useCallback } from 'react';
import { useToast } from './Toast';
import { analyticsService } from '../services/analytics';

const AnalyticsDashboard = ({ isAdminContext = false }) => {
  const { showError, showSuccess } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('7d');
  const [analytics, setAnalytics] = useState({
    overview: {
      totalViews: 0,
      uniqueVisitors: 0,
      totalClicks: 0,
      conversionRate: 0,
      bounceRate: 0,
      avgSessionDuration: 0
    },
    topPages: [],
    topLinks: [],
    userBehavior: {
      newVsReturning: { new: 0, returning: 0 },
      deviceTypes: { desktop: 0, mobile: 0, tablet: 0 },
      trafficSources: { direct: 0, organic: 0, social: 0, referral: 0 }
    },
    conversions: {
      signups: [],
      upgrades: [],
      totalRevenue: 0
    },
    realtimeUsers: 0
  });

  // Debug function to check available tokens
  const checkTokens = useCallback(() => {
    const userToken = localStorage.getItem('authToken');
    const adminToken = localStorage.getItem('adminToken');
    
    return {
      hasUserToken: !!userToken,
      hasAdminToken: !!adminToken,
      userTokenPrefix: userToken ? userToken.substring(0, 5) + '...' : 'none',
      adminTokenPrefix: adminToken ? adminToken.substring(0, 5) + '...' : 'none',
      isAdminContext
    };
  }, [isAdminContext]);

  useEffect(() => {
    // Log token info on mount
    console.log('AnalyticsDashboard - Token status:', checkTokens());
    loadAnalyticsData();
  }, [timeRange, checkTokens]);

  const loadAnalyticsData = async () => {
    setIsLoading(true);
    setError(''); // Clear any previous errors
    try {
      const { hasUserToken } = checkTokens();
      
      // Convert timeRange to days
      const daysMap = {
        '24h': 1,
        '7d': 7,
        '30d': 30,
        '90d': 90,
        'custom': 30 // Default for custom range
      };
      const days = daysMap[timeRange] || 7;

      console.log(`Loading analytics data for ${days} days in ${isAdminContext ? 'admin' : 'user'} context`);

      let adminData, summaryData;

      if (isAdminContext) {
        // Admin context - only fetch admin analytics, regardless of user token presence
        console.log('Admin context - fetching admin analytics only');
        [adminData] = await Promise.allSettled([
          analyticsService.getAdminAnalytics(days, true)
        ]);
        // Skip user analytics summary in admin context
        summaryData = { status: 'rejected', reason: new Error('Admin context - user analytics not requested') };
      } else {
        // User context - fetch user analytics summary only
        console.log('User context - fetching user analytics summary');
        [summaryData] = await Promise.allSettled([
          analyticsService.getAnalyticsSummary(days, false)
        ]);
        // Skip admin analytics in user context
        adminData = { status: 'rejected', reason: new Error('User context - admin analytics not available') };
      }

      // Handle successful and failed requests
      const admin = (adminData.status === 'fulfilled' && adminData.value) ? adminData.value : {};
      const summary = (summaryData.status === 'fulfilled' && summaryData.value) ? summaryData.value : {};

      // Log the status of each request with details (only log actual errors)
      if (adminData.status === 'rejected' && !adminData.reason.message.includes('User context')) {
        console.log('Admin analytics request: rejected -', adminData.reason);
      } else if (adminData.status === 'fulfilled') {
        console.log('Admin analytics request: fulfilled');
      }
      
      if (summaryData.status === 'rejected' && !summaryData.reason.message.includes('Admin context')) {
        console.log('Summary analytics request: rejected -', summaryData.reason);
      } else if (summaryData.status === 'fulfilled') {
        console.log('Summary analytics request: fulfilled');
      }
      
      console.log('Admin data sample:', admin ? JSON.stringify(admin).substring(0, 100) + '...' : 'empty');
      console.log('Summary data sample:', summary ? JSON.stringify(summary).substring(0, 100) + '...' : 'empty');
      
      // Check if we have any valid data based on context
      let hasValidData = false;
      let errorMessage = '';

      if (isAdminContext) {
        // In admin context, we need admin data to be available
        hasValidData = adminData.status === 'fulfilled' && Object.keys(admin).length > 0;
        if (!hasValidData) {
          if (adminData.reason?.response?.status === 405) {
            errorMessage = 'Admin analytics endpoint is not implemented in the backend.';
          } else if (adminData.reason?.response?.status === 401) {
            errorMessage = 'Admin token is invalid or expired.';
          } else {
            errorMessage = 'Failed to load admin analytics data.';
          }
        }
      } else {
        // In user context, we need summary data
        hasValidData = summaryData.status === 'fulfilled' && Object.keys(summary).length > 0;
        if (!hasValidData) {
          if (summaryData.reason?.response?.status === 401) {
            errorMessage = 'User authentication required for analytics data.';
          } else if (summaryData.reason?.response?.status === 404) {
            errorMessage = 'Analytics endpoint not found.';
          } else {
            errorMessage = 'Failed to load analytics data.';
          }
        }
      }

      if (!hasValidData) {
        setError(errorMessage);
        showError(errorMessage);
        setIsLoading(false);
        return;
      }

      // Clear any previous errors
      setError('');

      // Merge data with safe defaults
      const mergedData = {
        overview: {
          totalViews: admin.total_views || summary.total_views || 0,
          uniqueVisitors: admin.unique_visitors || summary.unique_visitors || 0,
          totalClicks: admin.total_clicks || summary.total_clicks || 0,
          conversionRate: admin.conversion_rate || summary.conversion_rate || 0,
          bounceRate: admin.bounce_rate || summary.bounce_rate || 0,
          avgSessionDuration: admin.avg_session_duration || summary.avg_session_duration || 0
        },
        topPages: admin.top_pages || summary.top_pages || [],
        topLinks: admin.top_links || summary.top_links || [],
        userBehavior: {
          newVsReturning: admin.new_vs_returning || summary.new_vs_returning || { new: 0, returning: 0 },
          deviceTypes: admin.device_types || summary.device_types || { desktop: 0, mobile: 0, tablet: 0 },
          trafficSources: admin.traffic_sources || summary.traffic_sources || { direct: 0, organic: 0, social: 0, referral: 0 }
        },
        conversions: {
          signups: admin.signups || summary.signups || [],
          upgrades: admin.upgrades || summary.upgrades || [],
          totalRevenue: admin.total_revenue || summary.total_revenue || 0
        },
        realtimeUsers: admin.realtime_users || summary.realtime_users || 0,
        daily: admin.daily || summary.daily || []
      };

      setAnalytics(mergedData);
      showSuccess('Analytics data loaded successfully');
    } catch (error) {
      console.error('Analytics loading error:', error);
      const errorMsg = `Failed to load analytics data: ${error.message}`;
      setError(errorMsg);
      showError(errorMsg);
      setAnalytics({
        overview: {
          totalViews: 0,
          uniqueVisitors: 0,
          totalClicks: 0,
          conversionRate: 0,
          bounceRate: 0,
          avgSessionDuration: 0
        },
        topPages: [],
        topLinks: [],
        userBehavior: {
          newVsReturning: { new: 0, returning: 0 },
          deviceTypes: { desktop: 0, mobile: 0, tablet: 0 },
          trafficSources: { direct: 0, organic: 0, social: 0, referral: 0 }
        },
        conversions: {
          signups: [],
          upgrades: [],
          totalRevenue: 0
        },
        realtimeUsers: 0,
        daily: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Debug Info (can be removed in production) */}
      <div className="text-xs text-gray-500 bg-gray-800 p-2 rounded mb-2">
        Context: {isAdminContext ? 'Admin' : 'User'} | 
        Auth: {checkTokens().hasUserToken ? `User (${checkTokens().userTokenPrefix})` : 'No User Token'} | 
        Admin: {checkTokens().hasAdminToken ? `Admin (${checkTokens().adminTokenPrefix})` : 'No Admin Token'}
      </div>
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">📊 Analytics Dashboard</h2>
        
        {/* Time Range Selector */}
        <div className="flex gap-2">
          {['24h', '7d', '30d', '90d'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Admin Notice - Show when no valid data and in error state */}
      {isAdminContext && !isLoading && error && (
        <div className="bg-red-900/20 border border-red-500 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="text-red-500">❌</div>
            <div className="text-red-400">
              <div className="font-semibold">Admin Analytics Unavailable</div>
              <div className="text-sm text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Users */}
      <div className="bg-green-900/20 border border-green-500 p-4 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-green-400 font-semibold">
            {analytics.realtimeUsers} users online right now
          </span>
        </div>
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-gray-400 text-sm font-medium mb-2">Total Page Views</h3>
          <div className="text-3xl font-bold text-white">{formatNumber(analytics.overview.totalViews)}</div>
          <div className="text-green-400 text-sm mt-1">📈 +12.5% vs last period</div>
        </div>

        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-gray-400 text-sm font-medium mb-2">Unique Visitors</h3>
          <div className="text-3xl font-bold text-white">{formatNumber(analytics.overview.uniqueVisitors)}</div>
          <div className="text-green-400 text-sm mt-1">📈 +8.3% vs last period</div>
        </div>

        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-gray-400 text-sm font-medium mb-2">Total Link Clicks</h3>
          <div className="text-3xl font-bold text-white">{formatNumber(analytics.overview.totalClicks)}</div>
          <div className="text-green-400 text-sm mt-1">🔗 +15.7% vs last period</div>
        </div>

        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-gray-400 text-sm font-medium mb-2">Conversion Rate</h3>
          <div className="text-3xl font-bold text-white">{analytics.overview.conversionRate}%</div>
          <div className="text-green-400 text-sm mt-1">💰 +2.1% vs last period</div>
        </div>

        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-gray-400 text-sm font-medium mb-2">Bounce Rate</h3>
          <div className="text-3xl font-bold text-white">{analytics.overview.bounceRate}%</div>
          <div className="text-red-400 text-sm mt-1">📉 +3.2% vs last period</div>
        </div>

        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-gray-400 text-sm font-medium mb-2">Avg. Session Duration</h3>
          <div className="text-3xl font-bold text-white">{formatDuration(analytics.overview.avgSessionDuration)}</div>
          <div className="text-green-400 text-sm mt-1">⏱️ +18s vs last period</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-white text-lg font-semibold mb-4">📄 Top Pages</h3>
          <div className="space-y-3">
            {(analytics.topPages || []).map((page, index) => (
              <div key={`page-${page.path || index}`} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-blue-600 rounded text-white text-xs flex items-center justify-center">
                    {index + 1}
                  </div>
                  <div>
                    <div className="text-white font-medium">{page.path || 'Unknown Page'}</div>
                    <div className="text-gray-400 text-sm">{page.uniqueViews || 0} unique views</div>
                  </div>
                </div>
                <div className="text-white font-semibold">{formatNumber(page.views || 0)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Links */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-white text-lg font-semibold mb-4">🔗 Top Performing Links</h3>
          <div className="space-y-3">
            {analytics.topLinks.map((link, index) => (
              <div key={`${link.title}-${index}`} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded text-white text-xs flex items-center justify-center">
                    {index + 1}
                  </div>
                  <div>
                    <div className="text-white font-medium">{link.title}</div>
                    <div className="text-gray-400 text-sm">@{link.profile}</div>
                  </div>
                </div>
                <div className="text-white font-semibold">{formatNumber(link.clicks)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* User Behavior */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* New vs Returning Users */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-white text-lg font-semibold mb-4">👥 User Types</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-300">New Users</span>
              <span className="text-blue-400 font-semibold">{analytics.userBehavior.newVsReturning.new}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full" 
                style={{ width: `${analytics.userBehavior.newVsReturning.new}%` }}
              ></div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Returning Users</span>
              <span className="text-green-400 font-semibold">{analytics.userBehavior.newVsReturning.returning}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${analytics.userBehavior.newVsReturning.returning}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Device Types */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-white text-lg font-semibold mb-4">📱 Device Types</h3>
          <div className="space-y-3">
            {Object.entries(analytics.userBehavior.deviceTypes || {}).map(([device, percentage]) => (
              <div key={device}>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-300 capitalize">{device}</span>
                  <span className="text-white font-semibold">{percentage}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full" 
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-white text-lg font-semibold mb-4">🌐 Traffic Sources</h3>
          <div className="space-y-3">            {Object.entries(analytics.userBehavior.trafficSources || {}).map(([source, percentage]) => (
              <div key={`source-${source}`}>
                <div className="flex justify-between mb-1">
                  <span className="text-gray-300 capitalize">{source}</span>
                  <span className="text-white font-semibold">{percentage || 0}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-yellow-500 h-2 rounded-full"
                    style={{ width: `${percentage || 0}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Conversions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sign-ups */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-white text-lg font-semibold mb-4">📝 Daily Sign-ups</h3>
          <div className="space-y-2">
            {(analytics.conversions.signups || []).map((day, index) => (
              <div key={`signup-${day.date || index}`} className="flex justify-between">
                <span className="text-gray-300">{day.date ? new Date(day.date).toLocaleDateString() : 'Unknown'}</span>
                <span className="text-blue-400 font-semibold">{day.count || 0} users</span>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h3 className="text-white text-lg font-semibold mb-4">
            💰 Revenue ({timeRange})
          </h3>
          <div className="text-3xl font-bold text-green-400 mb-4">
            ${analytics.conversions.totalRevenue}
          </div>
          <div className="space-y-2">
            {(analytics.conversions.upgrades || []).map((day, index) => (
              <div key={`upgrade-${day.date || index}`} className="flex justify-between">
                <span className="text-gray-300">{day.date ? new Date(day.date).toLocaleDateString() : 'Unknown'}</span>
                <span className="text-green-400 font-semibold">{day.count || 0} upgrades - ${day.revenue || 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
